import { MarketDataCardMenu } from '@src/components/MarketDataCard/MarketDataCardMenu';
import { MultilegComboType } from '@src/components/MultilegCombo/enums';
import { getMultilegInstrumentType } from '@src/components/OMS/NewOrder/utils/mappers';
import { useCloneMultilegSymbol } from '@src/hooks/useCloneMultilegSymbol';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import { useNavigateToDeepDiveOrMultileg } from '@src/hooks/useNavigateToDeepDiveOrMultileg';
import { useNavigateToMultilegCombo } from '@src/hooks/useNavigateToMultilegCombo';
import {
  ButtonVariants,
  isMultileg,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
  useMixpanel,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import type { PriceGridEntity } from '../../../PriceGridEntity';
import { getPriceGridColumnMarketSelectorValue } from '../common/getPriceGridColumnMarketSelectorValue';
import { getPriceGridMarketSelectorSelections } from '../common/getPriceGridMarketSelectorSelections';

export function PriceGridSymbolMenu({
  params,
}: {
  params: Pick<ICellRendererParams<PriceGridEntity>, 'colDef' | 'column' | 'data'>;
}) {
  const security = params.data?.security;
  const isInMobileMode = useIsMobileLayout();
  const mixpanel = useMixpanel();
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const marketSelectorValue = getPriceGridColumnMarketSelectorValue(params);
  const { selectedMarkets, selectedMarketAccounts } = getPriceGridMarketSelectorSelections(
    marketSelectorValue,
    marketsByName,
    marketAccountsByName
  );
  const aggregation = marketSelectorValue?.type === 'preset' ? marketSelectorValue.presetID : undefined;

  const navigateToDeepDiveOrMultileg = useNavigateToDeepDiveOrMultileg();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const cloneMultilegSymbol = useCloneMultilegSymbol(MixpanelEventSource.PriceGrid);

  const handleOpenDeepDive = useCallbackRef(() => {
    navigateToDeepDiveOrMultileg(security, selectedMarketAccounts, selectedMarkets, aggregation);
  });

  const handleCreateMultileg = useCallbackRef((multilegType: MultilegComboType) => {
    if (!security) {
      return;
    }
    mixpanel.track(MixpanelEvent.CreateNewMultileg, {
      [MixpanelEventProperty.Type]: multilegType,
      [MixpanelEventProperty.Source]: MixpanelEventSource.PriceGrid,
    });
    navigateToMultilegCombo({
      instrumentType: multilegType,
      marketAccountNames: selectedMarketAccounts,
      marketNames: selectedMarkets,
      legSymbols: [security.Symbol],
    });
  });

  const handleEditSymbol = useCallbackRef(() => {
    if (!security || !isMultileg(security)) {
      return;
    }
    mixpanel.track(MixpanelEvent.EditMultileg, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.PriceGrid,
    });
    navigateToMultilegCombo({
      instrumentType:
        getMultilegInstrumentType(security.MultilegDetails?.SyntheticProductType) ?? MultilegComboType.SyntheticCross,
      security,
      isEdit: true,
    });
  });

  if (!security) {
    return null;
  }

  return (
    <MarketDataCardMenu
      security={security}
      mixpanelSource={MixpanelEventSource.PriceGrid}
      triggerVariant={ButtonVariants.Default}
      usePortal
      onOpenDeepDive={handleOpenDeepDive}
      onCreateMultileg={isInMobileMode ? undefined : handleCreateMultileg}
      onEditSymbol={handleEditSymbol}
      onCloneSymbol={cloneMultilegSymbol}
    />
  );
}
